import React, { useState, useEffect } from 'react';
import {
  FiServer,
  FiDatabase,
  FiGlobe,
  FiHardDrive,
  FiCpu,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiRefreshCw
} from 'react-icons/fi';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { API_URL } from '../../../services/api';

interface HealthMetric {
  service_name: string;
  status: 'healthy' | 'warning' | 'critical' | 'degraded';
  response_time_ms?: number;
  last_checked: string;
  details?: Record<string, any>;
}

interface SystemService {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  metrics: HealthMetric[];
}

const SystemHealth: React.FC = () => {
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date().toLocaleString('th-TH'));
  const [healthData, setHealthData] = useState<HealthMetric[]>([]);

  // Fetch system health data from API
  useEffect(() => {
    fetchHealthData();
  }, [token]);

  const fetchHealthData = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/admin/dashboard/system-health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHealthData(data);
        setLastUpdate(new Date().toLocaleString('th-TH'));
      } else {
        setError(`API error: ${response.status}`);
      }
    } catch (err) {
      setError('ไม่สามารถเชื่อมต่อกับ API ได้');
      console.error('Failed to fetch health data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group health data by service type
  const groupHealthDataByService = (): SystemService[] => {
    const serviceMap: Record<string, SystemService> = {};

    healthData.forEach(metric => {
      const serviceType = getServiceType(metric.service_name);

      if (!serviceMap[serviceType.id]) {
        serviceMap[serviceType.id] = {
          id: serviceType.id,
          name: serviceType.name,
          icon: serviceType.icon,
          metrics: []
        };
      }

      serviceMap[serviceType.id].metrics.push(metric);
    });

    return Object.values(serviceMap);
  };

  // Determine service type from service name
  const getServiceType = (serviceName: string): { id: string; name: string; icon: React.ComponentType<any> } => {
    const nameLower = serviceName.toLowerCase();

    if (nameLower.includes('web') || nameLower.includes('server')) {
      return { id: 'web_server', name: 'Web Server', icon: FiServer };
    } else if (nameLower.includes('database') || nameLower.includes('mysql')) {
      return { id: 'database', name: 'Database', icon: FiDatabase };
    } else if (nameLower.includes('api') || nameLower.includes('endpoint')) {
      return { id: 'api_services', name: 'API Services', icon: FiGlobe };
    } else if (nameLower.includes('storage') || nameLower.includes('disk')) {
      return { id: 'storage', name: 'Storage', icon: FiHardDrive };
    } else {
      return { id: 'other', name: 'Other Services', icon: FiCpu };
    }
  };

  const systemServices = groupHealthDataByService();

  const getStatusIcon = (status: HealthMetric['status']) => {
    switch (status) {
      case 'healthy':
        return <FiCheckCircle className="h-5 w-5 text-green-600" />;
      case 'warning':
      case 'degraded':
        return <FiAlertCircle className="h-5 w-5 text-yellow-600" />;
      case 'critical':
        return <FiAlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <FiClock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: HealthMetric['status']) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'warning':
      case 'degraded':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusText = (status: HealthMetric['status']) => {
    switch (status) {
      case 'healthy':
        return 'ปกติ';
      case 'warning':
        return 'เตือน';
      case 'degraded':
        return 'ลดลง';
      case 'critical':
        return 'วิกฤต';
      default:
        return 'ไม่ทราบ';
    }
  };

  const getOverallStatus = (): HealthMetric['status'] => {
    if (healthData.length === 0) return 'healthy';

    const criticalCount = healthData.filter(m => m.status === 'critical').length;
    const warningCount = healthData.filter(m => m.status === 'warning' || m.status === 'degraded').length;

    if (criticalCount > 0) return 'critical';
    if (warningCount > 0) return 'warning';
    return 'healthy';
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchHealthData();
    setRefreshing(false);
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'เมื่อสักครู่';
    if (diffMins < 60) return `${diffMins} นาทีที่แล้ว`;
    return date.toLocaleString('th-TH');
  };

  const overallStatus = getOverallStatus();

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">สถานะระบบ</h3>
          <p className="text-sm text-gray-600 mt-1">กำลังโหลดข้อมูล...</p>
        </div>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-3">
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="h-24 bg-gray-200 rounded"></div>
                  <div className="h-24 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">สถานะระบบ</h3>
        </div>
        <div className="p-6">
          <div className="text-center py-12">
            <FiAlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 font-medium mb-2">เกิดข้อผิดพลาด</p>
            <p className="text-gray-500 text-sm mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              ลองอีกครั้ง
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center">
              <h3 className="text-lg font-semibold text-gray-900 mr-3">
                สถานะระบบ
              </h3>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(overallStatus)}`}>
                {getStatusIcon(overallStatus)}
                <span className="ml-2">{getStatusText(overallStatus)}</span>
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              ตรวจสอบสถานะและประสิทธิภาพของระบบ
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 disabled:opacity-50"
          >
            <FiRefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          อัพเดทล่าสุด: {lastUpdate}
        </div>
      </div>

      <div className="p-6">
        {systemServices.length === 0 ? (
          <div className="text-center py-12">
            <FiServer className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-sm">ไม่มีข้อมูลสถานะระบบ</p>
          </div>
        ) : (
          <div className="space-y-6">
            {systemServices.map((service) => (
              <div key={service.id} className="border-b border-gray-100 last:border-b-0 pb-6 last:pb-0">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-gray-100 rounded-lg mr-3">
                    <service.icon className="h-5 w-5 text-gray-600" />
                  </div>
                  <h4 className="text-md font-medium text-gray-900">{service.name}</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {service.metrics.map((metric, idx) => (
                    <div key={`${metric.service_name}-${idx}`} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          {getStatusIcon(metric.status)}
                          <span className="text-sm font-medium text-gray-900 ml-2">
                            {metric.service_name}
                          </span>
                        </div>
                        <div className="text-lg font-semibold text-gray-800 mb-1">
                          {getStatusText(metric.status)}
                          {metric.response_time_ms && (
                            <span className="text-sm font-normal text-gray-600 ml-2">
                              ({metric.response_time_ms}ms)
                            </span>
                          )}
                        </div>

                        {/* Additional details */}
                        {metric.details && Object.keys(metric.details).length > 0 && (
                          <div className="text-xs text-gray-500 space-y-1 mt-2">
                            {Object.entries(metric.details).map(([key, value]) => (
                              <div key={key}>
                                {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="text-right text-xs text-gray-500">
                        <FiClock className="h-3 w-3 inline mr-1" />
                        {formatTimestamp(metric.last_checked)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {overallStatus !== 'healthy' && (
        <div className="px-6 py-3 bg-yellow-50 border-t border-yellow-200 rounded-b-lg">
          <div className="flex">
            <FiAlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
            <div className="text-sm">
              <span className="font-medium text-yellow-800">
                พบปัญหาในระบบ
              </span>
              <span className="text-yellow-700 ml-2">
                กรุณาตรวจสอบและแก้ไขปัญหาที่พบ
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealth;